/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PreviewGroup, ScreenType } from 'client-code/previews';
import { Carousel, Carousels, FeedItem, OfferType } from 'client-code/wrs-feed-service';

import { APPLICATION_FEATURE_AREA, TIVO_SERVICES } from '../../config';
import { DeviceFeatures } from '../device';
import { DeviceModel } from '../device/DeviceModel';
import { PreviewsService, Services } from '../infrastructure';
import { WrsFeedService } from '../infrastructure/WrsFeedService';
import { Log } from '../logging';
import { TOfferPreview } from '../types/TOfferPreview';
import { TOfferPreviewCustomData } from '../types/TOfferPreviewCustomData';
import { TUnifiedCollectionPreview } from '../types/TUnifiedCollectionPreview';
import { TVodOfferPreview } from '../types/TVodOfferPreview';
import { TVodOfferPreviewCustomData } from '../types/TVodOfferPreviewCustomData';
import { ServicesClientImpl } from './ServicesClientImpl';

const TAG = 'HomeScreenInteractor';

export class HomeScreenInteractor extends ServicesClientImpl {
    private _commonParameters() {
        return {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        };
    }

    private _commonParametersWithTimeZone() {
        return {
            ...this._commonParameters(),
            timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
        };
    }

    public getHomeScreenData(
        screenName: string,
        count?: number,
        initialFeedItemCount?: number,
        pageCursor?: string,
    ): Promise<Carousels> {
        const service = new WrsFeedService({
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.WrsFeedService],
        });

        return service.fetchScreen(
            screenName,
            undefined,
            count,
            initialFeedItemCount,
            undefined,
            true,
            undefined,
            pageCursor,
        );
    }

    public getCarouselFeedItems(
        carouselName: string,
        offset: number,
        count: number,
    ): Promise<Carousel> {
        const service = new WrsFeedService({
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.WrsFeedService],
        });

        return service.fetchCarousel(carouselName, offset, count, undefined, true);
    }

    public getVodOfferPreview(vodOfferId: string): Promise<TVodOfferPreview> {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
        }).fetchVodOfferPreviewV2({
            vodOfferId,
            screenType: ScreenType.StandardBanner,
            groups: [PreviewGroup.Metadata],
        });
    }

    public getRecordingOfferPreview(recordingId: string): Promise<TOfferPreview> {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).fetchRecordingPreviewV2({
            recordingId,
            applicationFeatureArea: APPLICATION_FEATURE_AREA.HOME_SCREEN,
            groups: [PreviewGroup.Metadata],
        });
    }

    public getOfferPreview(offerId: string): Promise<TOfferPreview> {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).fetchOfferPreviewV2(offerId ?? '', ScreenType.StandardBanner);
    }

    /**
     * Fetches unified collection preview for Series tiles in the top10 carousel.
     * Used to retrieve internalRatingList for parental rating display.
     * @param unifiedCollectionId - The collection ID (feedItem.feedItemId for Series tiles)
     * @returns Promise resolving to TUnifiedCollectionPreview
     */
    public getUnifiedCollectionPreview(
        unifiedCollectionId: string,
    ): Promise<TUnifiedCollectionPreview> {
        return Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        }).getPreviewForUCId({
            unifiedCollectionId,
            groups: [PreviewGroup.Metadata],
        });
    }

    /**
     * Posts a watch history item for the given content.
     * @param watchHistoryParams - Parameters for posting watch history
     * @param watchHistoryParams.objectType - The object type for the watch history item
     * @param watchHistoryParams.objectId - The watch history item object ID
     * @param watchHistoryParams.collectionId - (Optional) The watch history item collection ID
     * @returns Promise that resolves when watch history is posted
     */
    public async addWatchHistory(
        objectType: OfferType,
        objectId: string,
        collectionId?: string,
    ): Promise<void> {
        try {
            const wrsFeedService = Services.getInstanceOf(WrsFeedService, {
                ...this._commonParametersWithTimeZone(),
                basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.WrsFeedService],
            });
            await wrsFeedService.addWatchHistory(objectType, objectId, collectionId);
            Log.i(
                HomeScreenInteractor.TAG,
                'Watch history posted successfully for objectId:',
                objectId,
            );
        } catch (error) {
            Log.e(HomeScreenInteractor.TAG, 'Error posting watch history:', error);
        }
    }

    public async getOfferPreviewForRecordOverlay(
        feedItem: FeedItem,
    ): Promise<TOfferPreviewCustomData | TVodOfferPreviewCustomData> {
        const previewsService = Services.getInstanceOf(PreviewsService, {
            ...this._commonParametersWithTimeZone(),
            basePath: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.PreviewsService],
            imageHostBaseUrl: ServicesClientImpl.servicesBaseUrls[TIVO_SERVICES.ImageHost],
        });
        switch (feedItem.offerType) {
            case OfferType.Live: {
                const res = await previewsService.fetchOfferPreviewV2(
                    feedItem.offerId ?? '',
                    ScreenType.All,
                );
                return res.offerPreview;
            }
            case OfferType.Vod:
            case OfferType.Socu: {
                const res = await previewsService.fetchVodOfferPreviewV2({
                    vodOfferId: feedItem.offerId ?? '',
                    screenType: ScreenType.FullBanner,
                });
                return res.vodOfferPreview;
            }
            case OfferType.Recording: {
                const res = await previewsService.fetchRecordingPreviewV3({
                    recordingId: feedItem.feedItemId,
                    applicationFeatureArea: APPLICATION_FEATURE_AREA.HOME_SCREEN,
                });
                return res.offerPreview;
            }
            default:
                return Promise.reject(new Error(`Unsupported offer type: ${feedItem.offerType}`));
        }
    }
}
